import { LoadingButton } from '@mui/lab';
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useReducer } from 'react';
import { useRecoilState } from 'recoil';
import { DateTime } from 'luxon';

import { useUpdateUserTermsMutation } from '../../api/apigQuery';
import { type IUserAtom, user } from '../../recoil/atoms/user';

// This is the date of the terms of service that the user must accept.
// It should align with the same version in kixie-powercall
// Currently: April 11, 2025
const TERMS_VERSION = new Date(Date.UTC(2025, 3, 11));

export const formatDateToYYYYMMDD = (date: Date): string =>
  DateTime.fromJSDate(date, { zone: 'utc' }).toFormat('yyyy-LL-dd');

const normalizeDateToYYYYMMDD = (dateString: string): string => {
  const dt = DateTime.fromISO(dateString, { zone: 'utc' });
  if (dt.isValid) {
    return dt.toFormat('yyyy-LL-dd');
  }
  // Fallback: try custom formats if ISO parsing fails
  const formats = ['yyyy-LL-dd', 'MM/dd/yyyy'];
  for (const f of formats) {
    const parsed = DateTime.fromFormat(dateString, f, { zone: 'utc' });
    if (parsed.isValid) return parsed.toFormat('yyyy-LL-dd');
  }
  throw new Error(`Invalid date string: ${dateString}`);
};

const initialState = {
  isModalOpen: false,
  acceptedTerms: false,
  acceptedPrivacyPolicy: false,
  isSubmitting: false,
  error: false,
};

type TermsGateState = typeof initialState;

type TermsGateAction =
  | { type: 'toggleTerms' | 'togglePrivacyPolicy' | 'reset' | 'error' }
  | { type: 'updateModal'; value: boolean }
  | { type: 'updateSubmitting'; value: boolean };

/**
 * Reducer function for managing the state of the TermsGate modal.
 *
 * Handles actions for toggling acceptance checkboxes, updating modal visibility, submission status, error state, and resetting to the initial state.
 *
 * @param state - The current state of the TermsGate modal.
 * @param action - The action describing the state update to perform.
 * @returns The updated TermsGate state after applying the action.
 */
function termsGateReducer(state: TermsGateState, action: TermsGateAction) {
  switch (action.type) {
    case 'toggleTerms':
      return { ...state, acceptedTerms: !state.acceptedTerms };
    case 'togglePrivacyPolicy':
      return { ...state, acceptedPrivacyPolicy: !state.acceptedPrivacyPolicy };
    case 'updateModal':
      return { ...state, isModalOpen: action.value };
    case 'updateSubmitting':
      return { ...state, isSubmitting: action.value };
    case 'error':
      return { ...state, isModalOpen: true, isSubmitting: false, error: true };
    case 'reset':
      return initialState;
    default:
      return state;
  }
}

const useStyles = makeStyles((theme: Theme) => ({
  dialogContentText: {
    marginBottom: theme.spacing(1),
  },
  errorText: {
    color: theme.palette.error.main,
    fontWeight: 600,
    marginTop: theme.spacing(1),
  },
  formControlLabel: {
    marginBottom: theme.spacing(0),
  },
  dialogTitle: {
    paddingBottom: theme.spacing(1),
  },
}));

/**
 * React component that enforces user acceptance of the latest Terms of Service and Privacy Policy before allowing further access.
 *
 * Displays a modal dialog requiring the user to confirm agreement to both documents. If the user's acceptance is missing or outdated, the modal is shown and the user cannot proceed until both checkboxes are checked and acceptance is submitted. On successful acceptance, updates the user's terms acceptance information in global state and localStorage.
 *
 * @returns The modal dialog UI for terms acceptance.
 */
function TermsGate() {
  const classes = useStyles();
  const [state, dispatch] = useReducer(termsGateReducer, initialState);
  const [updateUserTerms] = useUpdateUserTermsMutation();
  const [userAtomState, setUserAtom] = useRecoilState(user);

  const {
    isModalOpen,
    isSubmitting,
    acceptedTerms,
    acceptedPrivacyPolicy,
    error,
  } = state;

  useEffect(() => {
    if (isEmpty(userAtomState)) return;
    const { terms, accessToken, userid } = userAtomState as IUserAtom;
    const { acceptedAt, acceptedIp, versionAccepted } = terms ?? {};

    if (!accessToken || !userid) return;

    const missingRequiredFields = !acceptedIp || !acceptedAt || !versionAccepted;

    let isVersionOutdated = false;
    const currentTermsVersion = formatDateToYYYYMMDD(TERMS_VERSION);

    if (versionAccepted) {
      try {
        const normalizedVersionAccepted = normalizeDateToYYYYMMDD(versionAccepted);
        isVersionOutdated = normalizedVersionAccepted < currentTermsVersion;
      } catch (e) {
        console.error("Failed to parse versionAccepted date:", versionAccepted, e);
        isVersionOutdated = true;
      }
    }

    if (missingRequiredFields || isVersionOutdated) {
      dispatch({ type: 'updateModal', value: true });
    }
  }, [userAtomState]);

  async function handleAccept() {
    if (isEmpty(userAtomState)) return;
    const { apikey, userid, businessid } = userAtomState as IUserAtom;

    if (acceptedTerms && acceptedPrivacyPolicy) {
      dispatch({ type: 'updateSubmitting', value: true });

      try {
        const response = await updateUserTerms({
          userSettings: {
            apikey,
            businessId: businessid,
            userid,
          },
          termsVersionAccepted: formatDateToYYYYMMDD(TERMS_VERSION),
        }).unwrap();

        if (!response.success || !response.result || response.result.length === 0) {
          throw new Error("Failed to update user terms");
        }

        const { termsVersionAccepted, termsAcceptedAt, termsAcceptedIp } = response.result[0];
        const updatedUserAtom = {
          ...userAtomState,
          terms: {
            acceptedAt: termsAcceptedAt,
            acceptedIp: termsAcceptedIp,
            versionAccepted: normalizeDateToYYYYMMDD(termsVersionAccepted),
          },
        };
        setUserAtom(updatedUserAtom);
        localStorage.setItem('user', JSON.stringify(updatedUserAtom));

        dispatch({ type: 'reset' });
      } catch (apiError) {
        console.error('Terms acceptance failed:', apiError);
        dispatch({ type: 'error' });
      }
    }
  }

  return (
    <Dialog
      open={isModalOpen}
      keepMounted={false}
      aria-describedby="kixie-terms-of-service-and-privacy-policy-modal"
      data-qa="terms-gate-modal"
    >
      <DialogTitle className={classes.dialogTitle}>
        Please review and accept our Terms of Service & Privacy Policy
      </DialogTitle>
      <DialogContent>
        <DialogContentText className={classes.dialogContentText}>
          Welcome to Kixie! Before you can continue, we need you to confirm that
          you&apos;ve read and agree to our legal agreements.
        </DialogContentText>
        <FormGroup>
          <FormControlLabel
            className={classes.formControlLabel}
            control={
              <Checkbox
                data-qa="terms-gate-terms-service-checkbox"
                checked={acceptedTerms}
                disabled={isSubmitting}
                onChange={() => dispatch({ type: 'toggleTerms' })}
              />
            }
            label={
              <DialogContentText>
                I have read and agree to the{' '}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.kixie.com/terms"
                >
                  Terms of Service
                </a>
              </DialogContentText>
            }
          />
          <FormControlLabel
            className={classes.formControlLabel}
            control={
              <Checkbox
                data-qa="terms-gate-privacy-policy-checkbox"
                checked={acceptedPrivacyPolicy}
                disabled={isSubmitting}
                onChange={() => dispatch({ type: 'togglePrivacyPolicy' })}
              />
            }
            label={
              <DialogContentText>
                I have read and agree to the{' '}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.kixie.com/terms/privacy-policy/"
                >
                  Privacy Policy
                </a>
              </DialogContentText>
            }
          />
        </FormGroup>
        {error && (
          <DialogContentText data-qa="terms-gate-error-text" className={classes.errorText}>
            An error occurred while accepting the terms of service and privacy
            policy. Please try again.
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <LoadingButton
          data-qa="terms-gate-accept-button"
          variant="contained"
          color="primary"
          onClick={handleAccept}
          disabled={!acceptedTerms || !acceptedPrivacyPolicy}
          loading={isSubmitting}
        >
          Accept & Continue
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default TermsGate;
